import { defineStore } from "pinia"
import { apiClient } from "@/services/api"
import { backgroundPoll, mayRequestInBackground } from "@/services/backgroundRequests"
import { knownEntitlement } from "@/services/entitlements"
import { FEATURE_SETS } from "@/services/featureSet"
import { logger } from "@/services/logger"
import { isNavEntryEnabled } from "@/services/navigation"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"

const HEADERS = { tenant: true, authorization: true }

// A day a worker sends should show in the office within a minute; the count is one indexed query.
const COUNT_POLL_INTERVAL_MS = 60 * 1000

const countPoll = backgroundPoll(() => useDaySubmissionStore().loadCounts(), COUNT_POLL_INTERVAL_MS)

/**
 * Tagesmeldungen: the days the app's users sent to the site management. The overview's rows live here
 * (the table reads them), and how many are open, which the drawer's entry shows; a detail is the page's own.
 * Requests that can be refused for a reason the page or the report panel explains in place (a revision somebody
 * acted on meanwhile, a day's report changed since its draft) are `silent`, so they word it instead of the
 * snackbar.
 */
export const useDaySubmissionStore = defineStore("daySubmission", {
  state: () => ({
    rows: [] as any[],
    // The latest list request: an answer to an earlier one, for another status say, is left out
    rowsRequest: 0,
    openCount: 0,
  }),
  getters: {
    getRows: state => state.rows,
  },
  actions: {
    clearRows() {
      this.rows = []
    },
    async loadCounts() {
      // The Erklärung's frame has the sandbox answer it from the sample days, so its drawer shows the count
      // the list's walkthrough names
      if (!(IS_VIEW_HELP_EMBED || mayRequestInBackground())) return
      // A closed entry has no count. Cleared rather than kept, since the app bar shows the count without
      // asking the gate: a workspace that lost Tagesmeldungen, or a role that lost the rights, would keep
      // the last dot lit.
      if (!isNavEntryEnabled("nav-daysubmission")) {
        this.openCount = 0
        return
      }
      // The entry's gate lets the set through while the entitlements are unknown (services/entitlements.js),
      // and right after sign-in they are: /principal is not awaited. A count asked then would be refused for
      // a workspace without Tagesmeldungen, so it waits until /principal has said the workspace has them (the
      // drawer asks again once it has, LewNavigationBar). The frame's count never leaves the sandbox, so it is
      // not held to that.
      if (!IS_VIEW_HELP_EMBED) {
        const entitled = knownEntitlement(FEATURE_SETS.DAY_SUBMISSIONS)
        if (entitled === false) this.openCount = 0
        if (entitled !== true) return
      }
      try {
        const res = await apiClient.get("/day-submission/counts", { headers: HEADERS, silent: true, background: true })
        this.openCount = res.data?.open ?? 0
      } catch (error) {
        // The badge keeps the last count; a count nobody clicked for raises no snackbar, and one that got no
        // answer no Sentry event either. Unless the workspace no longer has Tagesmeldungen at all: then there
        // is nothing to count, and the app bar's dot would otherwise stay lit for a feature that is gone.
        if (error?.response?.data?.violationCode === "FEATURE_NOT_ENTITLED") this.openCount = 0
        logger.error(`Failed to load the Tagesmeldungen counts: ${error}`)
      }
    },
    /** The drawer polls while it is mounted; see `backgroundPoll`. */
    startCountPolling() {
      countPoll.start()
    },
    stopCountPolling() {
      countPoll.stop()
    },
    async loadRows(params: {
      page: number
      itemsPerPage: number
      search?: string
      sortBy?: string
      sortDesc?: boolean
      status?: string[]
      projectId?: string | null
      senderId?: string | null
      fromDate?: string | null
      toDate?: string | null
    }) {
      const request = ++this.rowsRequest
      const res = await apiClient.get("/day-submission", { params, headers: HEADERS })
      if (request !== this.rowsRequest) return res
      const content = res.data?.content ?? []
      this.rows = params.page === 0 ? content : [...this.rows, ...content]
      return res
    },
    async load(id: string) {
      const res = await apiClient.get(`/day-submission/${id}`, { headers: HEADERS })
      return res.data
    },
    async dismiss(id: string, revision: number, note: string | null) {
      const res = await apiClient.post(
        `/day-submission/${id}/dismiss`,
        { revision, note },
        { headers: HEADERS, silent: true }
      )
      this.loadCounts()
      return res.data
    },
    async reopen(id: string, revision: number) {
      const res = await apiClient.post(`/day-submission/${id}/reopen`, { revision }, { headers: HEADERS, silent: true })
      this.loadCounts()
      return res.data
    },
    /**
     * The Bautagesbericht the Tagesmeldung makes, for the editor: `projectId` only for one naming no site, and for
     * the day's report the shift its lines go to (`shiftIndex`, or `newShift`; else the one its times overlap most).
     */
    async constructionReportDraft(
      id: string,
      params: { projectId?: string | null; shiftIndex?: number | null; newShift?: boolean } = {}
    ) {
      const res = await apiClient.get(`/day-submission/${id}/draft/construction-report`, {
        params,
        headers: HEADERS,
        silent: true,
      })
      return res.data
    },
    async hourlyWageReportDraft(id: string, params: { projectId?: string | null } = {}) {
      const res = await apiClient.get(`/day-submission/${id}/draft/hourly-wage-report`, {
        params,
        headers: HEADERS,
        silent: true,
      })
      return res.data
    },
    /** Saves the report the editor holds as the Tagesmeldung's (`kind` `cr` or `hwr`), with the files added to it. */
    async saveReport(
      id: string,
      kind: "cr" | "hwr",
      body: { revision: number; base?: string | null; report: any },
      files: File[] = []
    ) {
      const path = `/day-submission/${id}/${kind === "cr" ? "construction-report" : "hourly-wage-report"}`
      let res
      if (files.length) {
        const form = new FormData()
        form.append("request", new Blob([JSON.stringify(body)], { type: "application/json" }))
        files.forEach(file => form.append("files", file))
        res = await apiClient.post(`${path}/with-files`, form, { headers: HEADERS, silent: true })
      } else {
        res = await apiClient.post(path, body, { headers: HEADERS, silent: true })
      }
      this.loadCounts()
      return res.data
    },
  },
})
